// State machine for Lightning. The actions here are the canonical mutations —
// in Phase 4 they'll be backed by Supabase (low-stakes via direct writes + RLS,
// high-stakes via Edge Functions).
import type { Action, AppState, Match, MatchEvent, MatchTeamStatus, QueueItem, RobotPhotoSet, RoundResult, Station, UserProfile } from '../types';
import { ROBOTS_BY_EVENT, robotClassLabel } from '../types';
import {
  initialMatches3v3, initialMatches1v1, initialARCTIC, initialEC,
  initialQueue, initialActiveInspection,
  initialWarnings, initialTeamRobotInspection, initialPreInspectionLevel,
  initialCheckIn, initialFieldCalibration, initialTeamPictures, initialTeamConfig, initialQualsLive,
  initialQualsBegun, initialPracticeMatches, teamById, TEAMS,
} from '../mock/data';
import { practiceAllowance } from './practice';
import { robotsForTeam } from './preInspection';
import { generateNextSwissRound, SWISS_TOTAL_ROUNDS } from './swiss';
import {
  DEFAULT_ARC_SCHEDULE, currentBlockFor, formatScheduledAt, nextBlockAfter, packMatchesIntoBlocks, resolveBlocks,
} from './schedule';

// Seed a CLEARED pre-inspection queue row for every robot the mock baseline
// shows at l3. They carry full passedStations, so they DON'T appear on the
// station desk (already done) but they DO mark that robot pre-inspected — which
// is what the event-scoped practice-booking gate reads. Robots below l3 are
// simply not seeded (they read as pending), so the mid-event desk stays clean.
function seedPreInspectionQueue(): QueueItem[] {
  const rows: QueueItem[] = [];
  for (const team of TEAMS) {
    const { robots, events } = robotsForTeam(team);
    robots.forEach((robotClass, i) => {
      const lvl = initialPreInspectionLevel[team.id]?.[events[i]]?.[robotClass] ?? 'pending';
      if (lvl !== 'l3') return;
      rows.push({
        id: `preinsp-${team.id}-${robotClass}`,
        team: team.id, event: events[i], robotClass,
        match: '', time: '', passedStations: ['s1', 's2', 's3'],
        busyAtStation: null, claimedBy: null, claimedAt: null,
        isBackup: false, preInspection: true, skipped: false, failed: false, urgent: false,
      });
    });
  }
  return rows;
}

// Mock Access Control data — dev/e2e only (real data comes from
// get_access_roster + the volunteer_emails table via SupabaseStore).
const initialAccessRoster: UserProfile[] = [
  { userId: 'u-tudor',   email: 'tudor.fanaru@arc-robotics.org', role: null,        effectiveRole: 'ops',       l3Override: false, captainsMeeting: false, lastSeenAt: '2026-06-19T14:00:00Z' },
  { userId: 'u-derek',   email: 'derek.wang@arc-robotics.org',   role: null,        effectiveRole: 'ops',       l3Override: false, captainsMeeting: false, lastSeenAt: '2026-06-19T13:30:00Z' },
  { userId: 'u-staff',   email: 'jordan.lee@arc-robotics.org',   role: null,        effectiveRole: 'staff',     l3Override: false, captainsMeeting: false, lastSeenAt: '2026-06-19T12:45:00Z' },
  { userId: 'u-marcus',  email: 'marcusyim2004@gmail.com',       role: 'volunteer', effectiveRole: 'volunteer', l3Override: true,  captainsMeeting: false, lastSeenAt: '2026-06-19T12:00:00Z' },
  { userId: 'u-natalie', email: 'natalie.santillano@gmail.com',  role: 'volunteer', effectiveRole: 'volunteer', l3Override: false, captainsMeeting: true,  lastSeenAt: null },
  // Authenticated-but-public (no role) — the roster hides these.
  { userId: 'u-public',  email: 'spectator@gmail.com',           role: null,        effectiveRole: null,        l3Override: false, captainsMeeting: false, lastSeenAt: '2026-06-19T11:00:00Z' },
];
const initialVolunteerEmails: string[] = [
  'marcusyim2004@gmail.com', 'natalie.santillano@gmail.com', 'tf_2013@live.com',
];

export const initialState: AppState = {
  matches3v3: initialMatches3v3,
  matches1v1: initialMatches1v1,
  arctic: initialARCTIC,
  ec: initialEC,
  queue: [...initialQueue, ...seedPreInspectionQueue()],
  activeInspection: initialActiveInspection,
  warnings: initialWarnings,
  officialDelay: { '3v3': 4, '1v1': 0, 'ec': 0 },
  // Anchor block IDs from the default ARC schedule. The 3v3 mock has 4 min
  // of delay accrued during Day 1 morning (D1-M); 1v1 + EC have none.
  officialDelayBlock: { '3v3': 'D1-M', '1v1': null, 'ec': null },
  pendingWarning: null,
  teamRobotInspection: initialTeamRobotInspection,
  preInspectionLevel: initialPreInspectionLevel,
  matchResults: {},  // mock starts with no recorded round outcomes
  matchNotes: {},    // mock starts with no recorded notes
  teamNotes: {},     // mock starts with no team notes
  refereeModules: {},    // mock starts with no borrowed modules
  refereeModuleLog: {},  // ...and no change log
  refereeOnLoan: {},     // ...and nothing on loan
  robotWeight: {},   // mock starts with no recorded weights
  checkIn: initialCheckIn,
  fieldCalibration: initialFieldCalibration,
  teamPictures: initialTeamPictures,
  teamConfig: initialTeamConfig,
  qualsLive: initialQualsLive,
  qualsBegun: initialQualsBegun,
  testMatch3v3: false,
  practiceMatches: initialPracticeMatches,
  // Mirror initialCheckIn's points_penalty_ec values into the public slice
  // so first-paint (mock store) shows the same data the schedule will read.
  ecPenalty: Object.fromEntries(
    Object.entries(initialCheckIn)
      .filter(([, ci]) => (ci.pointsPenaltyEc ?? 0) > 0)
      .map(([team, ci]) => [team, ci.pointsPenaltyEc!]),
  ),
  accessRoster: initialAccessRoster,
  volunteerEmails: initialVolunteerEmails,
};

// Helpers to operate on whichever event's matches array is targeted.
const matchesFor = (state: AppState, event: MatchEvent): Match[] =>
  event === '3v3' ? state.matches3v3 : state.matches1v1;

const setMatchesFor = (state: AppState, event: MatchEvent, matches: Match[]): AppState =>
  event === '3v3' ? { ...state, matches3v3: matches } : { ...state, matches1v1: matches };

// A real UUID for optimistic queue rows. inspection_queue.id is uuid-typed, so
// fabricated ids (e.g. "preinsp-<team>-<robot>") break any action that syncs by
// id (SELECT_QUEUE_ITEM, MARK_ROBOT). Minting the id client-side and inserting
// it explicitly keeps the optimistic id === the DB id — actions work right away
// AND survive the refetch (no stale-id swap).
function uuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// System-attributed timeline note. Used by auto-actions (RESCHEDULE,
// FORFEIT, SWAP) so the auto-generated entry shows up in the chat-style
// notes panel just like a manually-typed note. The matching DB-side
// trigger (migration 32) does the same thing on the server, so the
// optimistic note here gets reconciled with a server-stamped row on the
// next realtime echo.
function appendSystemNote(state: AppState, matchId: string, body: string): AppState {
  const tmpId = `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const note = {
    id: tmpId,
    matchId,
    body,
    authorEmail: 'me',  // SupabaseStore overwrites with real email on echo
    authorRole: null,
    createdAt: new Date().toISOString(),
  };
  const existing = state.matchNotes[matchId] ?? [];
  return {
    ...state,
    matchNotes: { ...state.matchNotes, [matchId]: [...existing, note] },
  };
}

// Team-notes counterpart of appendSystemNote — drops an auto-generated note on
// a team's timeline (e.g. an L3 inspection reset).
function appendTeamSystemNote(state: AppState, teamId: string, body: string): AppState {
  const tmpId = `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const note = {
    id: tmpId,
    teamId,
    body,
    authorEmail: 'me',  // SupabaseStore overwrites with real email on echo
    authorRole: null,
    createdAt: new Date().toISOString(),
  };
  const existing = state.teamNotes[teamId] ?? [];
  return {
    ...state,
    teamNotes: { ...state.teamNotes, [teamId]: [...existing, note] },
  };
}

// Synthesize a robot serial from the team id + robot class. Display-only —
// no logic depends on the format.
function synthSerial(team: string, robotClass: string): string {
  return `${team}-${robotClass.slice(0, 3).toUpperCase()}-001`;
}

// ISO timestamp → "H:MM AM/PM" display time, using UTC accessors (the app's
// wall-clock-as-UTC convention — same derivation SET_MATCH_TIME uses).
function displayTimeFromIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hour24 = d.getUTCHours();
  const min = d.getUTCMinutes();
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = (hour24 % 12) || 12;
  return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
}

// Build the per-station `cells` map for a queue item being inspected.
// 'pass' for any station already in passedStations; the active station gets
// 'active' (or 'fail' if the robot is currently flagged failed at this
// station's slot — surfaces the prior result so staff can RE-CHECK).
function buildCells(item: QueueItem, myStation: Station): { s1: 'pending' | 'active' | 'pass' | 'fail' | 'recheck'; s2: 'pending' | 'active' | 'pass' | 'fail' | 'recheck'; s3: 'pending' | 'active' | 'pass' | 'fail' | 'recheck' } {
  const cells = {
    s1: (item.passedStations.includes('s1') ? 'pass' : 'pending') as 'pending' | 'pass',
    s2: (item.passedStations.includes('s2') ? 'pass' : 'pending') as 'pending' | 'pass',
    s3: (item.passedStations.includes('s3') ? 'pass' : 'pending') as 'pending' | 'pass',
  } as { s1: 'pending' | 'active' | 'pass' | 'fail' | 'recheck'; s2: 'pending' | 'active' | 'pass' | 'fail' | 'recheck'; s3: 'pending' | 'active' | 'pass' | 'fail' | 'recheck' };
  if (cells[myStation] !== 'pass') {
    cells[myStation] = item.failed ? 'fail' : 'active';
  }
  return cells;
}

// Parse "9:10 AM" → minutes since midnight, for queue sorting. Returns 0 for
// missing/unparseable input — those land at the top, which is fine since they
// represent edge cases (just-inserted item before time was looked up).
// Exported for tests; internal helper otherwise.
export function parseQueueTime(time: string): number {
  if (!time) return 0;
  const m = /(\d+):(\d+)\s*(AM|PM)?/i.exec(time);
  if (!m) return 0;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const mer = (m[3] ?? '').toUpperCase();
  if (mer === 'PM' && h !== 12) h += 12;
  if (mer === 'AM' && h === 12) h = 0;
  return h * 60 + min;
}

// Sort queue by the underlying match's scheduled time. The DB-side fetch
// already orders by scheduled_at; this keeps the local optimistic state in
// the same order after inserts. Skipped rows always sort last regardless
// of time — they're out-of-active-attention. Tie-breaker: queue id, just
// to keep the sort stable across renders so React reconciliation doesn't
// thrash.
function sortQueueByTime(queue: QueueItem[]): QueueItem[] {
  return [...queue].sort((a, b) => {
    const aSkip = a.skipped ? 1 : 0;
    const bSkip = b.skipped ? 1 : 0;
    if (aSkip !== bSkip) return aSkip - bSkip;
    const dt = parseQueueTime(a.time) - parseQueueTime(b.time);
    return dt !== 0 ? dt : a.id.localeCompare(b.id);
  });
}

export function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'REORDER_QUEUE': {
      const fromIdx = state.queue.findIndex((q) => q.id === action.from);
      const toIdx = state.queue.findIndex((q) => q.id === action.to);
      if (fromIdx < 0 || toIdx < 0) return state;
      const next = [...state.queue];
      const [m] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, m);
      return { ...state, queue: next };
    }
    case 'SORT_QUEUE_BY_MATCH': {
      // Re-sort by match scheduled time. Lexicographic match-id sort would
      // misorder G10 vs G2; parse the time field instead.
      return { ...state, queue: sortQueueByTime(state.queue) };
    }
    case 'ADD_TO_QUEUE': {
      // Queue is per-(team, robot, event). A team is added by inserting one
      // entry per relevant robot class. ROBOTS_BY_EVENT × team's actual robots
      // gives the inspected list (e.g., a 1v1-only team has only Standard).
      if (state.queue.some((q) => !q.preInspection && q.team === action.team && q.event === action.event)) return state;

      // EC is single-team and doesn't go through the live/onDeck/upcoming
      // match flow — its "match" is the team's EC run from state.ec.
      // Insert one queue row for the Engineer robot AND flip the run's
      // status to at_inspection so the L3 panel + status pills track
      // the team through the same lifecycle as 1v1/3v3 (s1 → s2 → s3 →
      // l3 → l2 → in_game → completed). No urgency calc (EC runs in
      // its own block).
      if (action.event === 'ec') {
        const ecRun = state.ec.find((e) => e.team === action.team && !e.completed);
        if (!ecRun) return state;
        const newItem: QueueItem = {
          id: `q-${ecRun.id}-${action.team}-Engineer`,
          team: action.team,
          event: 'ec',
          robotClass: 'Engineer',
          match: ecRun.id,
          time: ecRun.time,
          passedStations: [],
          busyAtStation: null,
          claimedBy: null,
          isBackup: false,
          skipped: false,
          failed: false,
          urgent: false,
        };
        const ec = state.ec.map((e) =>
          e.id === ecRun.id ? { ...e, status: 'at_inspection' as const } : e,
        );
        return { ...state, ec, queue: sortQueueByTime([...state.queue, newItem]) };
      }

      const matches = matchesFor(state, action.event);
      const nextMatch = matches.find(
        (m) =>
          (m.red === action.team && m.redS === 'not_tracked') ||
          (m.blue === action.team && m.blueS === 'not_tracked'),
      );
      if (!nextMatch) return state;
      const isRed = nextMatch.red === action.team;
      const updatedMatches = matches.map((m) =>
        m.id === nextMatch.id
          ? { ...m, ...(isRed ? { redS: 'at_inspection' as const } : { blueS: 'at_inspection' as const }) }
          : m,
      );
      // Urgency: match is at most 2 slots after the on-deck (or live).
      const onDeckIdx = matches.findIndex((m) => m.onDeck);
      const liveIdx = matches.findIndex((m) => m.live);
      const refIdx = onDeckIdx >= 0 ? onDeckIdx : liveIdx;
      const matchIdx = matches.findIndex((m) => m.id === nextMatch.id);
      const urgent = refIdx >= 0 && matchIdx >= 0 && matchIdx - refIdx <= 2;
      // One QueueItem per robot class the team brings to this event, plus
      // an extra Standard row tagged isBackup if the team flies a backup
      // robot. Mirrors the SQL trigger (_inspection_queue_lifecycle) so
      // the optimistic UI matches what the server will compute.
      const teamRecord = teamById(action.team);
      const eventClasses = ROBOTS_BY_EVENT[action.event];
      const robotClasses = teamRecord.robots.filter((c) => eventClasses.includes(c));
      const newItems: QueueItem[] = robotClasses.map((robotClass) => ({
        id: `q-${nextMatch.id}-${action.team}-${robotClass}`,
        team: action.team,
        event: action.event,
        robotClass,
        match: nextMatch.id,
        time: nextMatch.time,
        passedStations: [],
        busyAtStation: null,
        claimedBy: null,
        isBackup: false,
        skipped: false,
        failed: false,
        urgent,
      }));
      // Backup-robot check is per-event check-in (mirror of the SQL trigger).
      if (state.checkIn[action.team]?.hasBackupRobot) {
        newItems.push({
          id: `q-${nextMatch.id}-${action.team}-Backup`,
          team: action.team,
          event: action.event,
          robotClass: 'Standard',
          match: nextMatch.id,
          time: nextMatch.time,
          passedStations: [],
          busyAtStation: null,
          claimedBy: null,
          isBackup: true,
          skipped: false,
          failed: false,
          urgent,
        });
      }
      return {
        ...setMatchesFor(state, action.event, updatedMatches),
        queue: sortQueueByTime([...state.queue, ...newItems]),
      };
    }
    case 'ADD_TO_PRE_INSPECTION': {
      // Enqueue EVERY robot the team brings (across all its events) for a
      // pre-quals run through the station desk. No match, no event split, no
      // L3 — these rows are backup-like (own per-station progress) so they
      // never touch the team's match inspection. No-op if already added.
      if (state.queue.some((q) => q.preInspection && q.team === action.team)) return state;
      const { robots, events } = robotsForTeam(teamById(action.team));
      if (robots.length === 0) return state;
      const newItems: QueueItem[] = robots.map((robotClass, i) => ({
        id: uuid(),         // real UUID so it matches the DB row id (synced by id)
        team: action.team,
        event: events[i],   // cosmetic — preinsp rows aren't rolled up by event
        robotClass,
        match: '',          // not tied to a match
        time: '',
        passedStations: [],
        busyAtStation: null,
        claimedBy: null,
        claimedAt: null,
        isBackup: false,
        preInspection: true,
        skipped: false,
        failed: false,
        urgent: false,
      }));
      return { ...state, queue: [...state.queue, ...newItems] };
    }
    case 'REMOVE_FROM_PRE_INSPECTION': {
      return {
        ...state,
        queue: state.queue.filter((q) => !(q.preInspection && q.team === action.team)),
      };
    }
    case 'SELECT_QUEUE_ITEM': {
      // Picks ONE (team, robot, event) row for the inspector clicking. Only
      // one robot is "in scope" at a time per inspector — matches the
      // physical process where each individual handles one robot at a time.
      // Claims are tracked per-USER (claimedBy) so two inspectors at the
      // same station can each hold their own claim without clobbering each
      // other; the legacy per-station logic broke when S2 had two staff.
      const item = state.queue.find((q) => q.id === action.queueId);
      if (!item) return state;
      const myStation = state.activeInspection.currentStation;
      if (myStation === 'l3') return state;  // L3 doesn't claim via this action
      // Anonymous (mock / not signed in) callers fall back to a single
      // synthetic owner so the legacy single-inspector flow still works.
      const me = action.userId ?? '__anon__';
      // Release my prior claim (anywhere) and claim this entry. Other
      // inspectors' claims — including at the same station — are untouched.
      const queue = state.queue.map((q) => {
        if (q.claimedBy === me && q.id !== action.queueId) {
          return { ...q, busyAtStation: null, claimedBy: null, claimedAt: null };
        }
        if (q.id === action.queueId) return { ...q, busyAtStation: myStation, claimedBy: me, claimedAt: new Date().toISOString() };
        return q;
      });
      return {
        ...state,
        queue,
        activeInspection: {
          team: item.team,
          match: item.match,
          robotClass: item.robotClass,
          // Specific row id — disambiguates primary vs backup of the
          // same robot_class. Without this, MARK_ROBOT was matching the
          // FIRST row by (team,match,robotClass) and silently mutating
          // the wrong row in the DB when both existed.
          queueId: item.id,
          serial: synthSerial(item.team, item.robotClass),
          currentStation: myStation,
          cells: buildCells(item, myStation),
        },
      };
    }
    case 'SET_QUEUE_ITEM_SKIPPED': {
      // Toggle the skipped flag on a queue row. Skipping releases any
      // station claim it had (no point holding S2 while sitting it out)
      // and re-sorts so skipped rows trail. Recoverable — un-skipping
      // restores the row at its original time-position with passedStations
      // intact.
      let queue = state.queue.map((q) => {
        if (q.id !== action.queueId) return q;
        if (action.skipped) {
          return { ...q, skipped: true, busyAtStation: null, claimedBy: null, claimedAt: null };
        }
        return { ...q, skipped: false };
      });
      // If the active inspection was tracking this row, drop it — operator
      // can pick a new row to inspect.
      const skipped = queue.find((q) => q.id === action.queueId)?.skipped;
      let active = state.activeInspection;
      if (skipped) {
        const item = state.queue.find((q) => q.id === action.queueId);
        if (item
            && active.team === item.team
            && active.match === item.match
            && active.robotClass === item.robotClass) {
          active = { ...active, team: '', match: '', robotClass: '', queueId: '', serial: '',
                     cells: { s1: 'pending', s2: 'pending', s3: 'pending' } };
        }
      }
      queue = sortQueueByTime(queue);
      return { ...state, queue, activeInspection: active };
    }
    case 'MARK_ROBOT': {
      const cs = state.activeInspection.currentStation;
      if (cs === 'l3') return state;
      const st: Station = cs;
      // Per-robot model: result applies to THIS one robot at THIS one station.
      // Pass moves the robot to the next station's queue (frees this slot).
      // FAIL is destructive — wipes ALL station progress and flags the row;
      // the robot has to walk back to S1 and start over (per ARC rules: a
      // failed mech check invalidates downstream wiring/software passes too).
      // Re-check / pending un-pass at this cell only.
      const newCell =
        action.result === 'pass'    ? 'pass' as const    :
        action.result === 'fail'    ? 'fail' as const    :
        action.result === 'recheck' ? 'recheck' as const : 'pending' as const;

      // Update the SPECIFIC queue row by id. Previously this filtered by
      // (team, match, robotClass) which matched both primary and backup
      // queue rows — corrupting whichever wasn't actually being inspected
      // and leaving stale busy_at_station on the other row in the DB
      // (because the supabase-actions sync also picked the first match by
      // tuple). Now backup and primary are correctly independent.
      const queue = state.queue.map((q) => {
        if (q.id !== state.activeInspection.queueId) return q;
        // Clear both claim fields together (DB invariant — busy_at_station
        // and claimed_by are set/cleared as a pair).
        const next = { ...q, busyAtStation: null as Station | null, claimedBy: null as string | null, claimedAt: null };
        if (newCell === 'pass') {
          // Add this station to passedStations; clear failed flag (this
          // station's outcome supersedes any prior failure on the row).
          next.passedStations = q.passedStations.includes(st)
            ? q.passedStations
            : [...q.passedStations, st];
          next.failed = false;
        } else if (newCell === 'fail') {
          // FAIL is total: wipe every prior station pass so the robot
          // re-enters at S1. Anything downstream depends on a valid
          // mech check, so it can't be trusted after a fail.
          next.passedStations = [];
          next.failed = true;
        } else {
          // Re-check / pending: drop only this station from passedStations.
          next.passedStations = q.passedStations.filter((s) => s !== st);
        }
        return next;
      });

      // Clear the NOW INSPECTING pane after marking. The robot has just been
      // returned to the appropriate queue — the next station on pass, this
      // station again on recheck, or S1 on fail — so the inspector explicitly
      // picks the next robot instead of the just-marked one lingering active.
      return {
        ...state,
        queue,
        activeInspection: {
          team: '',
          match: '',
          robotClass: '',
          queueId: '',
          serial: '',
          currentStation: st,
          cells: { s1: 'pending', s2: 'pending', s3: 'pending' },
        },
      };
    }
    case 'RESET_ROBOT_INSPECTION': {
      const { event, matchId, side, team, robotClass, isBackup } = action;
      if (!team) return state;
      const matches = matchesFor(state, event);
      const m = matches.find((mm) => mm.id === matchId);
      if (!m) return state;

      // 1. Reset this robot's persistent rollup → pending so it re-enters at S1.
      //    Backups don't live in the rollup (their progress is per queue row).
      let next = state;
      if (!isBackup) {
        const evRoll = { ...(state.teamRobotInspection[team]?.[event] ?? {}) };
        evRoll[robotClass] = 'pending';
        next = {
          ...next,
          teamRobotInspection: {
            ...next.teamRobotInspection,
            [team]: { ...(next.teamRobotInspection[team] ?? {}), [event]: evRoll },
          },
        };
      }

      // 2. Put the robot back at S1 in the queue. If the team still has queue
      //    rows (awaiting L3), reset just the target row in place. If it had
      //    left the desk (confirmed to L3 — rows dropped), rebuild the team's
      //    rows from the rollup (mirrors the inspection_queue_lifecycle trigger).
      const hasRows = next.queue.some((q) => q.team === team && q.match === matchId);
      if (hasRows) {
        next = {
          ...next,
          queue: next.queue.map((q) =>
            q.team === team && q.match === matchId && q.robotClass === robotClass && !!q.isBackup === !!isBackup
              ? { ...q, passedStations: [], failed: false, busyAtStation: null, claimedBy: null, claimedAt: null }
              : q,
          ),
        };
      } else {
        const passed = next.teamRobotInspection[team]?.[event] ?? {};
        const eventClasses = ROBOTS_BY_EVENT[event];
        const teamRobots = teamById(team).robots.filter((r) => eventClasses.includes(r));
        const stations: Station[] = ['s1', 's2', 's3'];
        const rebuilt: QueueItem[] = teamRobots.map((rc) => {
          const lvl = passed[rc];
          const n = lvl === 'l3' ? 3 : lvl === 'l2' ? 2 : lvl === 'l1' ? 1 : 0;
          return {
            id: `optim-${team}-${matchId}-${rc}-${Date.now()}`,
            team, event, robotClass: rc, match: matchId, time: m.time,
            passedStations: stations.slice(0, n),
            busyAtStation: null, claimedBy: null,
            isBackup: false, skipped: false, failed: false, urgent: false,
          };
        });
        if (state.checkIn[team]?.hasBackupRobot) {
          rebuilt.push({
            id: `optim-${team}-${matchId}-Backup-${Date.now()}`,
            team, event, robotClass: 'Standard', match: matchId, time: m.time,
            passedStations: [],
            busyAtStation: null, claimedBy: null,
            isBackup: true, skipped: false, failed: false, urgent: false,
          });
        }
        next = {
          ...next,
          queue: [...next.queue.filter((q) => !(q.team === team && q.match === matchId)), ...rebuilt],
        };
      }

      // 3. Revert the team's match side back into active inspection so the desk
      //    re-queues it if it had advanced past the stations (l3 / l2 / etc).
      const reverted = matches.map((mm) =>
        mm.id === matchId
          ? (side === 'red' ? { ...mm, redS: 'at_inspection' as const } : { ...mm, blueS: 'at_inspection' as const })
          : mm,
      );
      next = setMatchesFor(next, event, reverted);

      // 4. Stamp both timelines — the match's and the team's. The team note
      //    names the robot so it reads cleanly out of match context.
      next = appendSystemNote(next, matchId, `MATCH ${matchId}: Inspection Reset from L3`);
      next = appendTeamSystemNote(next, team, `${robotClassLabel(robotClass)} inspection reset to S1 from L3 (${matchId})`);
      return next;
    }
    case 'SET_STATION': {
      // Per-robot model: switching stations is purely a UI move. Reset the
      // active inspection (team/match/robot/queueId/serial/cells) so the
      // inspector lands on the new station with NO robot pre-selected —
      // forces them to pick from the queue explicitly. Otherwise the prior
      // robot stays visible at the new station and any dispatch (PASS /
      // RECHECK / FAIL) silently acts on the wrong row. The queue claim
      // (if any) stays in place; SELECT_QUEUE_ITEM will release it the
      // moment the inspector picks a new row.
      const newStation = action.station;
      return {
        ...state,
        activeInspection: {
          team: '',
          match: '',
          robotClass: '',
          queueId: '',
          serial: '',
          currentStation: newStation,
          cells: { s1: 'pending', s2: 'pending', s3: 'pending' },
        },
      };
    }
    case 'ADVANCE_MATCH': {
      // Capture the live match BEFORE rewriting; we need its id to attribute
      // round results. action.rounds may be empty (cold-start advance with
      // no live match yet) or a list (mark completed with outcomes).
      const oldLive = matchesFor(state, action.event).find((m) => m.live);
      const oldOnDeck = matchesFor(state, action.event).find((m) => m.onDeck);
      // Promotion gate: Up-Next only walks into Live if both teams are at
      // L2 (cleared inspection + L3 confirm). Anything earlier means the
      // robots haven't been cleared, so the live slot stays empty until
      // the operator advances again. Mirrors migration 52's server check.
      const onDeckReady = !!oldOnDeck && oldOnDeck.redS === 'l2' && oldOnDeck.blueS === 'l2';
      const matches = matchesFor(state, action.event).map((m) => {
        if (m.live) return { ...m, live: false, redS: 'completed' as const, blueS: 'completed' as const };
        if (m.onDeck && onDeckReady) return { ...m, onDeck: false, live: true, redS: 'in_game' as const, blueS: 'in_game' as const };
        return m;
      });
      // Pick new on-deck: chronologically earliest non-concluded match,
      // excluding whatever just became live (if any). Up Next follows the
      // schedule; readiness gates ADVANCE separately. Mirrors
      // _find_next_ready_match (migration 44) so the optimistic UI
      // matches what the server sets. When promotion was blocked, the
      // re-pick lands on the same Up-Next match — idempotent.
      const CONCLUDED: MatchTeamStatus[] = [
        'completed', 'forfeit_v', 'forfeit_iv', 'rescheduled', 'direct_transfer', 'in_game',
      ];
      const liveId = matches.find((m) => m.live)?.id;
      // Clear the prior on-deck flag IF we promoted — otherwise leave it
      // so the same match shows up below. Belt-and-braces: we re-set
      // onDeck on whatever the next pick is.
      let nextMatches = matches.map((m) => ({ ...m, onDeck: false }));
      const nextReadyIdx = nextMatches.findIndex((m) =>
        m.id !== liveId && !CONCLUDED.includes(m.redS) && !CONCLUDED.includes(m.blueS),
      );
      if (nextReadyIdx >= 0) {
        nextMatches[nextReadyIdx] = { ...nextMatches[nextReadyIdx], onDeck: true };
      }
      let next = setMatchesFor(state, action.event, nextMatches);
      // Store rounds keyed on the just-completed match's id. Empty rounds (e.g.
      // cold-start) is a no-op.
      if (oldLive && action.rounds && action.rounds.length > 0) {
        next = {
          ...next,
          matchResults: { ...next.matchResults, [oldLive.id]: action.rounds },
        };
      }
      // Advancing into a real 3v3 match turns off the test-match feed toggle so
      // the demo can't resurface when the live slot next empties (mirrors the
      // write in supabase-actions). 3v3-only — test_match_3v3 is a 3v3 concept.
      if (action.event === '3v3' && next.testMatch3v3) {
        next = { ...next, testMatch3v3: false };
      }
      return next;
    }
    case 'ADVANCE_TO_NEXT_MATCH': {
      // Head ref's manual advance off a BRIDGE-completed match. Unlike
      // ADVANCE_MATCH this records no rounds and doesn't complete the live match
      // — the bridge already finalized it (referee_record_round, migration 109,
      // no longer auto-advances). We just clear the finished match's live flag,
      // promote on-deck → live if both are at L2, and re-pick on-deck. Mirrors
      // the advance_to_next_match RPC; the server reconciles on the next fetch.
      const oldOnDeck = matchesFor(state, action.event).find((m) => m.onDeck);
      const onDeckReady = !!oldOnDeck && oldOnDeck.redS === 'l2' && oldOnDeck.blueS === 'l2';
      const promoted = matchesFor(state, action.event).map((m) => {
        if (m.live) return { ...m, live: false };  // already completed by the bridge
        if (m.onDeck && onDeckReady) return { ...m, onDeck: false, live: true, redS: 'in_game' as const, blueS: 'in_game' as const };
        return m;
      });
      const CONCLUDED: MatchTeamStatus[] = [
        'completed', 'forfeit_v', 'forfeit_iv', 'rescheduled', 'direct_transfer', 'in_game',
      ];
      const liveId = promoted.find((m) => m.live)?.id;
      const nextMatches = promoted.map((m) => ({ ...m, onDeck: false }));
      const nextReadyIdx = nextMatches.findIndex((m) =>
        m.id !== liveId && !CONCLUDED.includes(m.redS) && !CONCLUDED.includes(m.blueS),
      );
      if (nextReadyIdx >= 0) {
        nextMatches[nextReadyIdx] = { ...nextMatches[nextReadyIdx], onDeck: true };
      }
      const advanced = setMatchesFor(state, action.event, nextMatches);
      // Advancing into a real 3v3 match turns off the test-match feed (see the
      // ADVANCE_MATCH case above for why).
      return action.event === '3v3' && advanced.testMatch3v3
        ? { ...advanced, testMatch3v3: false }
        : advanced;
    }
    case 'COMPLETE_EC_MATCH': {
      // Optimistic: store rounds in matchResults using the EC convention
      // (winner='red', red_score=score, blue_score=0) and mark the
      // matching ECRun as completed. The server's ec_complete_match RPC
      // does the canonical write; fetchAll reconciles.
      const rounds: RoundResult[] = action.rounds.map((r) => ({
        winner: 'red' as const,
        redScore: r.score,
        blueScore: 0,
      }));
      const ec = state.ec.map((run) =>
        run.id === action.matchId ? { ...run, completed: true, live: false } : run,
      );
      return {
        ...state,
        ec,
        matchResults: { ...state.matchResults, [action.matchId]: rounds },
      };
    }
    case 'SAVE_MATCH_ROUNDS': {
      // Partial-round save (mid-play) or ops correction. Doesn't move the
      // live/on-deck slot — just records what's been scored so far. Server
      // (save_match_rounds RPC) recomputes winner_side from these rounds
      // and updates match_state.winner_side on the next fetch.
      return {
        ...state,
        matchResults: { ...state.matchResults, [action.matchId]: action.rounds },
      };
    }
    case 'REQUEST_REPLAY': {
      // Server-side: request_replay RPC creates the new match row and
      // bumps the schedule. The next fetchAll picks up the new match
      // and updated scheduled_ats; until then the optimistic state is a
      // no-op (we don't know the new id's exact scheduled_at without
      // re-running the schedule packer client-side).
      return state;
    }
    case 'ADD_DELAY': {
      const cur = state.officialDelay[action.event] ?? 0;
      // Anchor the delay to the current block. If the previous anchor was a
      // different (older) block, the delay value was effectively zero before
      // this action; reset cur to 0 so we accumulate fresh from this block.
      const blocks = resolveBlocks(DEFAULT_ARC_SCHEDULE);
      const matches = matchesFor(state, action.event);
      const curBlock = currentBlockFor(matches, blocks);
      const anchor = curBlock?.id ?? null;
      const prevAnchor = state.officialDelayBlock[action.event];
      const baseline = prevAnchor === anchor ? cur : 0;
      const nextMin = Math.max(0, baseline + (action.minutes ?? 5));
      return {
        ...state,
        officialDelay: { ...state.officialDelay, [action.event]: nextMin },
        officialDelayBlock: { ...state.officialDelayBlock, [action.event]: anchor },
      };
    }
    case 'CLEAR_DELAY':
      return {
        ...state,
        officialDelay:      { ...state.officialDelay,      [action.event]: 0 },
        officialDelayBlock: { ...state.officialDelayBlock, [action.event]: null },
      };
    case 'SWAP_NEXT_MATCH': {
      // Swap LIVE and ON_DECK — running on-deck first; current match becomes next.
      // No delay incurred (vs. RESCHEDULE which shifts subsequent slots).
      const matches = [...matchesFor(state, action.event)];
      const liveIdx = matches.findIndex((m) => m.live);
      const odIdx = matches.findIndex((m) => m.onDeck);
      if (liveIdx === -1 || odIdx === -1) return state;
      const live = matches[liveIdx], od = matches[odIdx];
      matches[liveIdx] = { ...live, live: false, onDeck: true,  redS: 'l2',      blueS: 'l2' };
      matches[odIdx]   = { ...od,   onDeck: false, live: true,   redS: 'in_game', blueS: 'in_game' };
      // Auto-notes go onto the timeline rather than match.notes — server
      // trigger (migration 32) mirrors the DB-side current_notes write.
      let next = setMatchesFor(state, action.event, matches);
      next = appendSystemNote(next, live.id, 'Swapped — runs after ' + od.id);
      next = appendSystemNote(next, od.id,   'Swapped forward');
      return next;
    }
    case 'RESCHEDULE_MATCH': {
      const beforeMatches = matchesFor(state, action.event);
      const original = beforeMatches.find((m) => m.id === action.matchId);
      if (!original) return state;
      const afterTarget = beforeMatches.find((m) => m.id === action.afterId);
      // New slot = afterTarget's nominal time + the event's match duration.
      // Computed for the chat note + as the rescheduled match's new
      // scheduledAt so the upcoming list shows it in the right slot.
      const durationMin = DEFAULT_ARC_SCHEDULE.matchDurationMinutes[
        action.event === '3v3' ? 'qual_3v3' : 'qual_1v1'
      ] ?? 25;
      const newScheduledAt = afterTarget?.scheduledAt
        ? new Date(Date.parse(afterTarget.scheduledAt) + durationMin * 60_000).toISOString().replace(/\.\d{3}Z$/, 'Z')
        : undefined;
      const oldTimeLabel = original.scheduledAt
        ? formatScheduledAt(original.scheduledAt)
        : original.time;
      const newTimeLabel = newScheduledAt
        ? formatScheduledAt(newScheduledAt)
        : `after ${action.afterId}`;
      // Was this the live match? If so we need to promote the on-deck
      // slot — otherwise the Live card stays anchored on the rescheduled
      // match (same pattern FORFEIT_MATCH uses).
      const wasLive = !!original.live;

      // Reset both sides to not_tracked. The match has moved to a future
      // slot; the teams need to come back through the queue → inspection
      // when their new time approaches. We used to stamp the team statuses
      // as 'rescheduled' (terminal-looking), but that left the match in
      // limbo — filtered out of the upcoming list, excluded from
      // _find_next_ready_match, and visually treated like a forfeit even
      // though it's still pending. The audit trail lives in the chat note
      // ("Rescheduled: <old> → <new>") and server-side rescheduled_to.
      //
      // Cascade: pushing G4 into G8's slot would collide. So shift every
      // match whose nominal time is >= the new slot forward by one
      // duration — single pass on the pre-mutation timestamps so there's
      // no double-shift. Matches before the new slot (including the ones
      // between G4's old slot and the new slot) stay put; the empty slot
      // G4 vacated is left as a gap rather than compacting upward, which
      // would silently move the after-target itself and surprise ops.
      const newScheduledAtMs = newScheduledAt ? Date.parse(newScheduledAt) : null;
      let matches = beforeMatches.map((m) => {
        if (m.id === action.matchId) {
          return {
            ...m,
            redS: 'not_tracked' as const,
            blueS: 'not_tracked' as const,
            live: false,
            onDeck: false,
            scheduledAt: newScheduledAt ?? m.scheduledAt,
            time: newScheduledAt ? formatScheduledAt(newScheduledAt) : m.time,
          };
        }
        if (newScheduledAtMs !== null && m.scheduledAt) {
          const mMs = Date.parse(m.scheduledAt);
          if (mMs >= newScheduledAtMs) {
            const shifted = new Date(mMs + durationMin * 60_000).toISOString().replace(/\.\d{3}Z$/, 'Z');
            return { ...m, scheduledAt: shifted, time: formatScheduledAt(shifted) };
          }
        }
        return m;
      });
      const moved = matches.find((m) => m.id === action.matchId)!;
      matches = matches.filter((m) => m.id !== action.matchId);
      const afterIdx = matches.findIndex((m) => m.id === action.afterId);
      matches.splice(afterIdx + 1, 0, moved);

      // If the rescheduled match was live, promote the on-deck match in
      // its place — but only if both sides are at L2 (matches the server's
      // advance_match gate added in migration 52). Otherwise the live slot
      // stays empty and on-deck stays put, so ops can see what's blocking
      // rather than ramming an unready team into 'in_game'.
      if (wasLive) {
        const liveIdx = matches.findIndex((m) => m.live);
        if (liveIdx === -1) {
          const odIdx = matches.findIndex((m) => m.onDeck);
          if (odIdx >= 0) {
            const od = matches[odIdx];
            if (od.redS === 'l2' && od.blueS === 'l2') {
              matches[odIdx] = { ...od, live: true, onDeck: false, redS: 'in_game', blueS: 'in_game' };
              if (matches[odIdx + 1]) matches[odIdx + 1] = { ...matches[odIdx + 1], onDeck: true };
            }
            // else: on-deck stays on-deck. Live card renders empty until
            // the on-deck teams clear inspection and ops takes action.
          }
        }
      }

      const noteBody = `Rescheduled: ${oldTimeLabel} → ${newTimeLabel}`;
      return appendSystemNote(setMatchesFor(state, action.event, matches), action.matchId, noteBody);
    }
    case 'FORFEIT_MATCH': {
      // Pre-announced forfeits let ops forfeit a future (non-live) match, so
      // capture whether THIS match was the live one before mutating — on-deck
      // promotion must only follow the live match concluding, never a future
      // forfeit (which would otherwise start the next match during a lull).
      const target = matchesFor(state, action.event).find((m) => m.id === action.matchId);
      const wasLive = !!target?.live;
      const wasOnDeck = !!target?.onDeck;
      const matches = matchesFor(state, action.event).map((m) => {
        if (m.id !== action.matchId) return m;
        const losing = action.side;
        return {
          ...m,
          live: false,
          // Clear the up-next slot too — forfeiting the on-deck match must not
          // leave the forfeited match sitting in UP NEXT.
          onDeck: false,
          redS:  losing === 'red'  ? ('forfeit_v' as const) : ('completed' as const),
          blueS: losing === 'blue' ? ('forfeit_v' as const) : ('completed' as const),
        };
      });
      // L2 gate before promoting on-deck. Same reasoning as RESCHEDULE_MATCH
      // above and as advance_match (migration 52). Mirrors forfeit_match's
      // server-side `was_live` guard (migration 61).
      if (wasLive) {
        const odIdx = matches.findIndex((m) => m.onDeck);
        if (odIdx >= 0) {
          const od = matches[odIdx];
          if (od.redS === 'l2' && od.blueS === 'l2') {
            matches[odIdx] = { ...od, live: true, onDeck: false, redS: 'in_game', blueS: 'in_game' };
            if (matches[odIdx + 1]) matches[odIdx + 1] = { ...matches[odIdx + 1], onDeck: true };
          }
        }
      }
      // Forfeiting the UP-NEXT match empties the on-deck slot — re-pick the next
      // non-concluded match into it (mirrors _find_next_ready_match + the
      // forfeit_match RPC's on-deck re-pick). Skip if a live promotion above
      // already filled it. Live is untouched (a future forfeit never starts a match).
      if (wasOnDeck && !matches.some((m) => m.onDeck)) {
        const liveId = matches.find((m) => m.live)?.id;
        const CONCLUDED: MatchTeamStatus[] = [
          'completed', 'forfeit_v', 'forfeit_iv', 'rescheduled', 'direct_transfer', 'in_game',
        ];
        const nextIdx = matches.findIndex((m) =>
          m.id !== liveId && !CONCLUDED.includes(m.redS) && !CONCLUDED.includes(m.blueS));
        if (nextIdx >= 0) matches[nextIdx] = { ...matches[nextIdx], onDeck: true };
      }
      const noteBody = (action.side === 'red' ? 'Red' : 'Blue') + ' voluntary forfeit';
      return appendSystemNote(setMatchesFor(state, action.event, matches), action.matchId, noteBody);
    }
    case 'SET_MATCH_STATUS': {
      // EC is single-team and stored as ECRun (not Match), so it can't go
      // through the matches3v3 / matches1v1 path. Update the EC run's
      // status directly. The lifecycle trigger on the server will manage
      // queue rows; we just mirror the status optimistically.
      if (action.event === 'ec') {
        const ec = state.ec.map((e) =>
          e.id === action.matchId ? { ...e, status: action.status } : e,
        );
        return { ...state, ec };
      }
      const oldMatches = matchesFor(state, action.event);
      const oldMatch = oldMatches.find((m) => m.id === action.matchId);
      if (!oldMatch) return state;
      const oldStatus = action.side === 'red' ? oldMatch.redS : oldMatch.blueS;
      const newStatus = action.status;
      const team = action.side === 'red' ? oldMatch.red : oldMatch.blue;

      const newMatches = oldMatches.map((m) =>
        m.id === action.matchId
          ? (action.side === 'red' ? { ...m, redS: newStatus } : { ...m, blueS: newStatus })
          : m,
      );
      let next = setMatchesFor(state, action.event, newMatches);

      // Mirror the inspection_queue_lifecycle DB trigger optimistically. Without
      // this, sending a 1v1 team back to inspection from Match Control would
      // leave the queue UI stale until the realtime echo arrived (and could be
      // missed entirely if the echo races against another fetchAll).
      // Dedup is per (team, match) — a team CAN have queue rows for both their
      // 3v3 and 1v1 matches simultaneously.
      const ACTIVE_INSPECTION: readonly QueueItem['team'][] | readonly string[] = ['at_inspection', 's1', 's2', 's3'];
      const wasActive = (ACTIVE_INSPECTION as readonly string[]).includes(oldStatus);
      const isActive = (ACTIVE_INSPECTION as readonly string[]).includes(newStatus);
      if (team) {
        if (wasActive && !isActive) {
          // Transitioned out of active inspection (to l3, l2, in_game, …):
          // drop ALL queue rows for this (team, match). Per-robot model has
          // N rows per (team, match); they all exit together.
          next = {
            ...next,
            queue: next.queue.filter((q) => !(q.team === team && q.match === action.matchId)),
          };
        } else if (isActive && oldStatus !== newStatus) {
          // Transitioned INTO active inspection from anywhere else: ensure
          // a queue row exists for each of the team's event-relevant robots
          // at this (team, match). Skip if any rows already exist — the
          // team is mid-flow and re-creating would trample progress.
          const exists = next.queue.some((q) => q.team === team && q.match === action.matchId);
          if (!exists) {
            const passed = state.teamRobotInspection[team]?.[action.event] ?? {};
            const eventClasses = ROBOTS_BY_EVENT[action.event];
            const teamRobots = teamById(team).robots.filter((r) => eventClasses.includes(r));
            // Per-robot rows. passedStations is computed PER ROBOT from its
            // rollup level (l1 → s1 passed, l2 → s1+s2, l3 → all).
            const stations: Station[] = ['s1', 's2', 's3'];
            const newItems: QueueItem[] = teamRobots.map((robotClass) => {
              const lvl = passed[robotClass];
              const passedStations: Station[] = [];
              const passedCount =
                lvl === 'l3' ? 3 :
                lvl === 'l2' ? 2 :
                lvl === 'l1' ? 1 : 0;
              for (let i = 0; i < passedCount; i++) passedStations.push(stations[i]);
              return {
                id: `optim-${team}-${action.matchId}-${robotClass}-${Date.now()}`,
                team,
                event: action.event,
                robotClass,
                match: action.matchId,
                time: oldMatch.time,
                passedStations,
                busyAtStation: null,
                claimedBy: null,
                isBackup: false,
                skipped: false,
                failed: false,
                urgent: false,
              };
            });
            // Backup row — empty passedStations regardless of rollup, since
            // backup inspection doesn't carry across matches. Gated on
            // check-in flag (set by ops when the team checks in for the day).
            if (state.checkIn[team]?.hasBackupRobot) {
              newItems.push({
                id: `optim-${team}-${action.matchId}-Backup-${Date.now()}`,
                team,
                event: action.event,
                robotClass: 'Standard',
                match: action.matchId,
                time: oldMatch.time,
                passedStations: [],
                busyAtStation: null,
                claimedBy: null,
                isBackup: true,
                skipped: false,
                failed: false,
                urgent: false,
              });
            }
            next = { ...next, queue: sortQueueByTime([...next.queue, ...newItems]) };
          }
        }
      }
      return next;
    }
    case 'SWAP_MATCHES': {
      const matches = [...matchesFor(state, action.event)];
      const aIdx = matches.findIndex((m) => m.id === action.matchAId);
      const bIdx = matches.findIndex((m) => m.id === action.matchBId);
      if (aIdx < 0 || bIdx < 0 || aIdx === bIdx) return state;
      const a = matches[aIdx];
      const b = matches[bIdx];
      // Swap times AND positions: the earlier slot gets B with A's old time,
      // the later slot gets A with B's old time. Net effect: each match takes
      // the other's scheduled position, and the array stays time-sorted.
      const [earlyIdx, lateIdx] = aIdx < bIdx ? [aIdx, bIdx] : [bIdx, aIdx];
      const [early, late] = aIdx < bIdx ? [a, b] : [b, a];
      matches[earlyIdx] = { ...late, time: early.time, scheduledAt: early.scheduledAt };
      matches[lateIdx]  = { ...early, time: late.time, scheduledAt: late.scheduledAt };
      // Timeline notes on both matches so the swap is visible in chat
      // (matches the SWAP_NEXT_MATCH pattern above). Server-side migration
      // 65 mirrors this via current_notes; the mirror trigger lands them
      // in match_notes for the canonical timeline.
      let next = setMatchesFor(state, action.event, matches);
      next = appendSystemNote(next, action.matchAId, `Swapped with ${action.matchBId}`);
      next = appendSystemNote(next, action.matchBId, `Swapped with ${action.matchAId}`);
      return next;
    }
    case 'ADD_WARNING': {
      const w = action.warning;
      const id = 'W' + (state.warnings.length + 13);
      return { ...state, warnings: [{ id, ...w, at: 'just now' }, ...state.warnings], pendingWarning: null };
    }
    case 'SET_PENDING_WARNING':
      return { ...state, pendingWarning: action.prefill };
    case 'CLEAR_PENDING_WARNING':
      return { ...state, pendingWarning: null };
    case 'SET_PRE_INSPECTION_LEVEL': {
      // Pre-Inspection screen / Teams → Robots tab. Updates the standalone
      // preInspectionLevel state — does NOT touch teamRobotInspection (the
      // live desk rollup) or any per-match state.
      const teamRecord = state.preInspectionLevel[action.team] ?? {};
      const eventRecord = teamRecord[action.event] ?? {};
      return {
        ...state,
        preInspectionLevel: {
          ...state.preInspectionLevel,
          [action.team]: {
            ...teamRecord,
            [action.event]: { ...eventRecord, [action.robot]: action.status },
          },
        },
      };
    }
    case 'GENERATE_BRACKET':
      // Server-side RPC inserts 15 playoff matches and updates event_state.
      // Locally we no-op; the realtime echo + fetchAll will pick them up.
      // (Optimistic insertion of all 15 playoff matches client-side would
      // duplicate a fair amount of the server's seeding logic for marginal UX
      // benefit — bracket generation is a one-shot transition, not a hot path.)
      return state;
    case 'GENERATE_SWISS_ROUND': {
      // Append the next Swiss round to matches1v1. Pure-function pairing
      // (src/state/swiss.ts) — no server round-trip needed since the algorithm
      // is deterministic given (matches, results, teams).
      // No-op if already at the round cap or fewer than 2 teams to pair.
      const result = generateNextSwissRound(state.matches1v1, state.matchResults, TEAMS, SWISS_TOTAL_ROUNDS);
      if (result.matches.length === 0) return state;
      return { ...state, matches1v1: [...state.matches1v1, ...result.matches] };
    }
    case 'APPEND_MATCH_NOTE': {
      // Optimistic insert into the in-memory notes timeline. The supabase
      // action assigns the real UUID on insert; we use a tmp_-prefixed id
      // so the realtime echo can dedupe by content+window if needed (for
      // now realtime simply re-fetches the table, replacing tmp ids with
      // server ids). Empty/whitespace bodies are dropped.
      const body = action.body.trim();
      if (!body) return state;
      const note = {
        id: `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        matchId: action.matchId,
        body,
        authorEmail: 'me',  // overwritten by SupabaseStore on echo with real email
        authorRole: null,   // ditto
        createdAt: new Date().toISOString(),
      };
      const existing = state.matchNotes[action.matchId] ?? [];
      return {
        ...state,
        matchNotes: { ...state.matchNotes, [action.matchId]: [...existing, note] },
      };
    }
    case 'APPEND_TEAM_NOTE': {
      // Mirror of APPEND_MATCH_NOTE: optimistic insert with a temp id.
      // SupabaseStore's realtime echo replaces it on next fetch with the
      // server-assigned uuid + canonical author info.
      const body = action.body.trim();
      if (!body) return state;
      const note = {
        id: `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        teamId: action.teamId,
        body,
        authorEmail: 'me',
        authorRole: null,
        createdAt: new Date().toISOString(),
      };
      const existing = state.teamNotes[action.teamId] ?? [];
      return {
        ...state,
        teamNotes: { ...state.teamNotes, [action.teamId]: [...existing, note] },
      };
    }
    case 'UPDATE_REFEREE_MODULES': {
      // Optimistic: merge the new per-module counts and append the change-log
      // line. SupabaseStore's realtime echo replaces the temp log id with the
      // server uuid + real author on next fetch.
      const prevModules = state.refereeModules[action.team] ?? {};
      const nextModules = { ...prevModules };
      for (const { module, counts } of action.modules) nextModules[module] = counts;
      // Keep the public on-loan projection in sync (server derives it from a
      // view; mirror it here so the team page tab + mock stay consistent).
      const onLoan: Record<string, number> = {};
      for (const [key, c] of Object.entries(nextModules)) {
        const n = c.refCheckout + c.exchangeExtras - c.refReturn;
        if (n > 0) onLoan[key] = n;
      }
      const logEntry = {
        id: `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        teamId: action.team,
        body: action.logBody,
        authorEmail: 'me',
        authorRole: null,
        createdAt: new Date().toISOString(),
      };
      const log = state.refereeModuleLog[action.team] ?? [];
      return {
        ...state,
        refereeModules: { ...state.refereeModules, [action.team]: nextModules },
        refereeModuleLog: { ...state.refereeModuleLog, [action.team]: [...log, logEntry] },
        refereeOnLoan: { ...state.refereeOnLoan, [action.team]: onLoan },
      };
    }
    case 'SET_ROBOT_WEIGHT': {
      // Sets / clears the LAST-KNOWN kg for a (team, event, robot_class,
      // instance) tuple. Captured at S1 inspection; Inspection.tsx enforces
      // mandatory entry before allowing PASS. `instance` defaults to
      // 'primary'; the backup Standard passes 'backup' so it no longer
      // overwrites the primary's weight. Stored on
      // robot_inspection_station.weight_kg via supabase-actions.
      const instance: import('../types').RobotInstance = action.instance ?? 'primary';
      const next = { ...state.robotWeight };
      const teamMap = { ...(next[action.team] ?? {}) };
      const eventMap = { ...((teamMap[action.event] as Record<string, Partial<Record<import('../types').RobotInstance, import('../types').RobotWeightEntry>>> | undefined) ?? {}) };
      const classMap = { ...(eventMap[action.robot] ?? {}) };
      if (action.kg == null) {
        delete classMap[instance];
      } else {
        // Stamp the recorded-at time so the resolver can pick the latest
        // Infantry weight across both events. Caller may pass `at` (e.g. the
        // DB set_at); otherwise use the current time for the optimistic write.
        classMap[instance] = { kg: action.kg, at: action.at ?? new Date().toISOString() };
      }
      // Drop the class entry entirely when no instance weights remain, so
      // `robotWeight[team][event][class]` is undefined (not {}) — keeps the
      // `?? undefined` reads clean and the persisted shape tight.
      if (Object.keys(classMap).length === 0) {
        delete eventMap[action.robot];
      } else {
        eventMap[action.robot] = classMap;
      }
      teamMap[action.event] = eventMap;
      next[action.team] = teamMap;
      return { ...state, robotWeight: next };
    }
    case 'SET_MATCH_TIME': {
      // Ops escape hatch: directly set a match's scheduledAt. Used when the
      // automatic packer / push-to-next-block doesn't capture what ops needs
      // (e.g. moving a match across a special-cased block, or to an exact
      // wall-clock time during a one-off schedule disruption). Does NOT
      // re-pack other matches — caller is responsible for any cascade.
      const eventMatches = matchesFor(state, action.event);
      const target = eventMatches.find((m) => m.id === action.matchId);
      if (!target) return state;
      const at = action.scheduledAt;
      // Re-derive display time from the new ISO timestamp.
      const d = new Date(at);
      if (Number.isNaN(d.getTime())) return state;
      const hour24 = d.getUTCHours();
      const min = d.getUTCMinutes();
      const period = hour24 >= 12 ? 'PM' : 'AM';
      const hour12 = (hour24 % 12) || 12;
      const time = `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
      const updated = eventMatches.map((m) =>
        m.id === action.matchId ? { ...m, scheduledAt: at, time } : m,
      );
      return setMatchesFor(state, action.event, updated);
    }
    case 'MOVE_TO_NEXT_BLOCK': {
      // Push a single match (and the cascade of subsequent matches) to the
      // next schedule block. Used when the live block has run so long that
      // the targeted match no longer fits before the break.
      // Algorithm:
      //   1. Locate the match's current block from its scheduledAt.
      //   2. Repack the targeted match + every later qual in this event into
      //      blocks starting from the next block. Earlier matches are left
      //      alone.
      //   3. Apply new scheduledAt + recomputed `time` strings.
      // Concluded matches (completed/forfeit) are skipped — they keep their
      // historical timestamps. Playoff matches are also skipped (different
      // scheduling regime).
      const blocks = resolveBlocks(DEFAULT_ARC_SCHEDULE);
      const eventMatches = matchesFor(state, action.event);
      const target = eventMatches.find((m) => m.id === action.matchId);
      if (!target?.scheduledAt) return state;
      const targetBlock = blocks.find(
        (b) => target.scheduledAt! >= b.starts && target.scheduledAt! < b.ends,
      );
      if (!targetBlock) return state;
      const next = nextBlockAfter(targetBlock, blocks);
      if (!next) return state;
      const playoffPrefix = action.event === '3v3' ? 'U_' : 'E_';
      const concluded = new Set(['completed', 'forfeit_v', 'forfeit_iv', 'rescheduled', 'direct_transfer']);
      // Repack candidates: the target + every later non-concluded qual in
      // chronological order. Anything before the target stays put.
      const candidates = eventMatches
        .filter((m) =>
          !m.id.startsWith(playoffPrefix) &&
          !concluded.has(m.redS) && !concluded.has(m.blueS) &&
          (m.id === target.id || (m.scheduledAt && m.scheduledAt > target.scheduledAt!)),
        )
        .sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));
      const dur = DEFAULT_ARC_SCHEDULE.matchDurationMinutes[action.event === '3v3' ? 'qual_3v3' : 'qual_1v1'] ?? 25;
      const items = candidates.map((m) => ({ id: m.id, durationMin: dur }));
      const { assigned } = packMatchesIntoBlocks(items, blocks, { from: next.starts });
      const newAtById = new Map(assigned.map((a) => [a.id, a.scheduledAt]));
      const updatedMatches = eventMatches.map((m) => {
        const at = newAtById.get(m.id);
        if (!at) return m;
        // Re-derive display time from new scheduledAt for legacy `time` reads.
        const d = new Date(at);
        const hour24 = d.getUTCHours();
        const min = d.getUTCMinutes();
        const period = hour24 >= 12 ? 'PM' : 'AM';
        const hour12 = (hour24 % 12) || 12;
        const time = `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
        return { ...m, scheduledAt: at, time };
      });
      return setMatchesFor(state, action.event, updatedMatches);
    }
    case 'UPDATE_CHECKIN': {
      const cur = state.checkIn[action.team] ?? {
        spielGiven: false, badges: 0, badges1v1: false, badges3v3: false, borrowing: false, checkInForm: false, pointsPenalty3v3: 0, pointsPenaltyEc: 0,
      };
      // Keep the public-readable ecPenalty slice in lockstep when the
      // edited field is pointsPenaltyEc. The slice is what the EC
      // schedule reads (public can't see check_in directly), so without
      // this mirror, ops edits would only appear after a server round-trip.
      const nextEcPenalty = action.field === 'pointsPenaltyEc'
        ? { ...state.ecPenalty, [action.team]: Number(action.value) || 0 }
        : state.ecPenalty;
      return {
        ...state,
        checkIn: {
          ...state.checkIn,
          [action.team]: { ...cur, [action.field]: action.value },
        },
        ecPenalty: nextEcPenalty,
      };
    }
    case 'SET_TEAM_PICTURE': {
      // Vol+ flips one photo checkbox or edits one name on the Team Pictures
      // page. Nested-merge so the team's other events/robots are untouched;
      // create a fresh empty record when this (team, event, robot) is first
      // touched. Server mirror: a vol+-gated upsert into team_pictures.
      const teamRec = state.teamPictures[action.team] ?? {};
      const eventRec = teamRec[action.event] ?? {};
      const cur: RobotPhotoSet = eventRec[action.robot] ?? {
        robot: false,
        driver: false, driverName: '', driverPronouns: '',
        chief: false, chiefName: '', chiefPronouns: '',
      };
      return {
        ...state,
        teamPictures: {
          ...state.teamPictures,
          [action.team]: {
            ...teamRec,
            [action.event]: {
              ...eventRec,
              [action.robot]: { ...cur, [action.field]: action.value },
            },
          },
        },
      };
    }
    case 'TOGGLE_FIELD_CALIBRATION': {
      // Ops flips one (team, event) calibration flag. Nested-merge so the
      // team's other event is untouched. Server mirror: an ops-gated upsert
      // into field_calibration (supabase-actions).
      const teamCal = state.fieldCalibration[action.team] ?? {};
      return {
        ...state,
        fieldCalibration: {
          ...state.fieldCalibration,
          [action.team]: { ...teamCal, [action.event]: action.done },
        },
      };
    }
    case 'GO_LIVE_QUALS_3V3': {
      // Captain's-Meeting commit for 3v3. Writes each team's group letter
      // into state.teamConfig, replaces matches3v3 with the freshly-built
      // round-robin schedule, and flips qualsLive['3v3']. One-way — the
      // UI gates further edits via the flag. Server mirror is the atomic
      // go_live_quals_3v3 RPC.
      const nextConfig = { ...state.teamConfig };
      for (const [teamId, group] of Object.entries(action.groups)) {
        nextConfig[teamId] = { ...(nextConfig[teamId] ?? {}), group };
      }
      return {
        ...state,
        teamConfig: nextConfig,
        matches3v3: action.matches,
        qualsLive: { ...state.qualsLive, '3v3': true },
      };
    }
    case 'GO_LIVE_QUALS_1V1': {
      // Captain's-Meeting commit for 1v1. Position in seededTeams is the
      // seed value (1-indexed). Replaces matches1v1 with the Swiss
      // Round-1 schedule built from those seeds.
      const nextConfig = { ...state.teamConfig };
      action.seededTeams.forEach((teamId, idx) => {
        nextConfig[teamId] = { ...(nextConfig[teamId] ?? {}), initialSeed1v1: idx + 1 };
      });
      return {
        ...state,
        teamConfig: nextConfig,
        matches1v1: action.matches,
        qualsLive: { ...state.qualsLive, '1v1': true },
      };
    }
    case 'CREATE_PRACTICE_MATCH': {
      // Referee books a pre-quals scrimmage. Same guards as the server RPC
      // (create_practice_match): practice window open for the event, distinct
      // teams, per-team allowance (1× 3v3 OR ≤2× 1v1, no mixing). The id is
      // a tmp_ placeholder — the realtime echo replaces it with the server's
      // P<n> once the RPC lands, same convention as optimistic notes.
      if (state.qualsBegun[action.event]) return state;
      if (!state.qualsLive[action.event]) return state;
      if (action.red === action.blue || !action.red || !action.blue) return state;
      if (!practiceAllowance(state.practiceMatches, action.red, action.event).ok) return state;
      if (!practiceAllowance(state.practiceMatches, action.blue, action.event).ok) return state;
      const at = action.scheduledAt ?? new Date().toISOString();
      const match: import('../types').PracticeMatch = {
        id: `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        event: action.event,
        time: displayTimeFromIso(at),
        scheduledAt: at,
        red: action.red,
        blue: action.blue,
        status: 'not_tracked',
      };
      return { ...state, practiceMatches: [...state.practiceMatches, match] };
    }
    case 'SET_PRACTICE_STATUS': {
      const idx = state.practiceMatches.findIndex((m) => m.id === action.matchId);
      if (idx < 0) return state;
      const next = [...state.practiceMatches];
      next[idx] = { ...next[idx], status: action.status };
      return { ...state, practiceMatches: next };
    }
    case 'SET_PRACTICE_TIME': {
      // Reschedule a booked practice match. Same wall-clock-as-UTC display
      // derivation as SET_MATCH_TIME.
      const idx = state.practiceMatches.findIndex((m) => m.id === action.matchId);
      if (idx < 0) return state;
      if (Number.isNaN(Date.parse(action.scheduledAt))) return state;
      const next = [...state.practiceMatches];
      next[idx] = {
        ...next[idx],
        scheduledAt: action.scheduledAt,
        time: displayTimeFromIso(action.scheduledAt),
      };
      return { ...state, practiceMatches: next };
    }
    case 'CANCEL_PRACTICE_MATCH': {
      if (!state.practiceMatches.some((m) => m.id === action.matchId)) return state;
      // Drop the booking AND any recorded rounds — a cancelled scrimmage
      // should leave no score behind (the server RPC deletes match_rounds
      // for the same reason). Frees both teams' allowance implicitly,
      // since allowance counts rows in practiceMatches.
      const nextResults = { ...state.matchResults };
      delete nextResults[action.matchId];
      return {
        ...state,
        practiceMatches: state.practiceMatches.filter((m) => m.id !== action.matchId),
        matchResults: nextResults,
      };
    }
    case 'RECORD_PRACTICE_RESULT': {
      // Score + complete in one step. Rounds land in matchResults under the
      // P<n> id — qual standings only aggregate ids present in
      // matches3v3/matches1v1, so practice scores stay decorative.
      const idx = state.practiceMatches.findIndex((m) => m.id === action.matchId);
      if (idx < 0) return state;
      const next = [...state.practiceMatches];
      next[idx] = { ...next[idx], status: 'completed' };
      return {
        ...state,
        practiceMatches: next,
        matchResults: { ...state.matchResults, [action.matchId]: action.rounds },
      };
    }
    case 'BEGIN_QUALS': {
      // One-way: closes the practice window for the event and surfaces the
      // published qual schedule in Match Control. Requires the Captain's
      // Meeting to have published (there must be a schedule to begin).
      if (!state.qualsLive[action.event]) return state;
      if (state.qualsBegun[action.event]) return state;
      // Pre-inspection is over once quals start — clear any leftover
      // pre-inspection rows so they don't clutter the live station queue.
      // (Match-based rows stay; those drive inspection during quals.)
      return {
        ...state,
        qualsBegun: { ...state.qualsBegun, [action.event]: true },
        queue: state.queue.filter((q) => !q.preInspection),
      };
    }
    case 'TOGGLE_TEST_MATCH': {
      // Ops toggle — the 3v3 referee/OB feed serves the demo (game_id 9999) when
      // on. Optimistic; supabase-actions writes event_state.test_match_3v3.
      return { ...state, testMatch3v3: action.on };
    }
    case 'SET_USER_ACCESS': {
      // Optimistic roster edit. The server (set_user_access RPC) is the source
      // of truth; the change only affects the target user's permissions on
      // their next token refresh / sign-in.
      return {
        ...state,
        accessRoster: state.accessRoster.map((u) =>
          u.userId === action.userId
            // Setting an explicit role override also pins the effective role;
            // a null override (e.g. just toggling a permission) leaves it as-is.
            ? { ...u, role: action.role, effectiveRole: action.role ?? u.effectiveRole, l3Override: action.l3Override, captainsMeeting: action.captainsMeeting }
            : u),
      };
    }
    case 'ADD_VOLUNTEER_EMAIL': {
      const email = action.email.trim().toLowerCase();
      if (!email || state.volunteerEmails.includes(email)) return state;
      return { ...state, volunteerEmails: [...state.volunteerEmails, email].sort() };
    }
    case 'REMOVE_VOLUNTEER_EMAIL': {
      const email = action.email.trim().toLowerCase();
      return { ...state, volunteerEmails: state.volunteerEmails.filter((e) => e !== email) };
    }
    default:
      // Defensive: unknown action types pass through unchanged. Action is a
      // discriminated union so TS already enforces exhaustiveness at compile time;
      // this guards against runtime drift (e.g. realtime payloads in Phase 3).
      return state;
  }
}
